import { APP_STORE_URL } from '@/utils/appStore'

/**
 * The in-app events, one page per App Store in-app event.
 *
 * Each `eventId` is the real App Store Connect event ID, so CTAs deep-link
 * to the event's own store listing (?eventid=). IDs and schedules verified
 * against ASC on July 14, 2026 (first three) and October 18, 2026 (last three,
 * submitted with 1.1.1 and waiting for review).
 */
export interface AppEvent {
  slug: string
  /** ASC in-app event ID (drives ?eventid= on the store URL). */
  eventId: string
  name: string
  badge: 'Challenge' | 'Major Update'
  /** Human-readable schedule, kept in sync with ASC territory schedules. */
  starts: string
  ends: string
  hook: string
  /** The event's ASC short description, overlaid on the card artwork. */
  shortDescription: string
  /** ASC publish start (ISO). Before this, ?eventid= URLs 404 on the web
   *  store, so CTAs fall back to the plain app URL until then. */
  publishAtIso: string
  /** ASC event end (ISO). Drives the Upcoming / Live / Ended label. */
  endsAtIso: string
  /** ASC event start (ISO). */
  startsAtIso: string
}

export const EVENTS: AppEvent[] = [
  {
    slug: 'plastic-free-july',
    eventId: '6790543336',
    name: 'Plastic Free July',
    badge: 'Challenge',
    starts: 'July 19, 2026',
    ends: 'July 31, 2026',
    hook: 'Scan what you eat and drink, find lower-plastic swaps, and watch your exposure drop.',
    shortDescription: 'Scan your daily habits and cut the plastic.',
    publishAtIso: '2026-07-18T00:00:00Z',
    startsAtIso: '2026-07-19T00:00:00Z',
    endsAtIso: '2026-07-31T23:59:00Z',
  },
  {
    slug: 'a-whole-new-microplastics',
    eventId: '6790543145',
    name: 'A Whole New MicroPlastics',
    badge: 'Major Update',
    starts: 'August 2, 2026',
    ends: 'August 17, 2026',
    hook: 'A cleaner look, instant scanning with no account, your Plastic Passport, and matched swaps.',
    shortDescription: 'Redesigned, faster, easier to scan.',
    publishAtIso: '2026-08-01T00:00:00Z',
    startsAtIso: '2026-08-02T00:00:00Z',
    endsAtIso: '2026-08-17T23:59:00Z',
  },
  {
    slug: 'back-to-school-lunchbox-check',
    eventId: '6790543243',
    name: 'Back to School Lunchbox Check',
    badge: 'Challenge',
    starts: 'August 16, 2026',
    ends: 'September 7, 2026',
    hook: 'Scan the lunchbox, the bottle and the containers before term starts.',
    shortDescription: 'Scan the lunchbox before term starts.',
    publishAtIso: '2026-08-05T00:00:00Z',
    startsAtIso: '2026-08-16T00:00:00Z',
    endsAtIso: '2026-09-07T23:59:00Z',
  },
  {
    slug: 'scan-it-score-it-swap-it',
    eventId: '6822775860',
    name: 'Scan it. Score it. Swap it.',
    badge: 'Major Update',
    starts: 'November 9, 2026',
    ends: 'December 6, 2026',
    hook: 'Scan food, drinks, containers or beauty items, see what drives each plastic-contact score, and try a lower-plastic swap.',
    shortDescription: 'A faster scan, a clearer score, matched swaps.',
    publishAtIso: '2026-11-02T15:00:00Z',
    startsAtIso: '2026-11-09T15:00:00Z',
    endsAtIso: '2026-12-06T15:00:00Z',
  },
  {
    slug: 'bottled-water-week',
    eventId: '6823711360',
    name: 'Bottled Water Week',
    badge: 'Challenge',
    starts: 'November 23, 2026',
    ends: 'November 30, 2026',
    hook: 'Scan the bottled water you drink this week, see its plastic-contact score, and compare it with tap water in steel.',
    shortDescription: 'Compare bottled water to a refill routine.',
    publishAtIso: '2026-11-16T15:00:00Z',
    startsAtIso: '2026-11-23T08:00:00Z',
    endsAtIso: '2026-11-30T08:00:00Z',
  },
  {
    slug: 'new-year-kitchen-reset',
    eventId: '6823711288',
    name: 'New Year Kitchen Reset',
    badge: 'Challenge',
    starts: 'December 28, 2026',
    ends: 'January 4, 2027',
    hook: 'Scan what you microwave or keep leftovers in, find your top driver, and pick one lower-plastic swap for the new year.',
    shortDescription: 'Check what you heat and store food in.',
    publishAtIso: '2026-12-21T15:00:00Z',
    startsAtIso: '2026-12-28T08:00:00Z',
    endsAtIso: '2027-01-04T08:00:00Z',
  },
]

export type EventStatus = 'upcoming' | 'live' | 'ended'

export function eventStatus(e: AppEvent, now: Date = new Date()): EventStatus {
  if (now < new Date(e.startsAtIso)) return 'upcoming'
  if (now > new Date(e.endsAtIso)) return 'ended'
  return 'live'
}

/** Live first, then upcoming (soonest first), then ended (most recent first). */
export function eventsForDisplay(now: Date = new Date()): AppEvent[] {
  const rank = { live: 0, upcoming: 1, ended: 2 } as const
  return [...EVENTS].sort((a, b) => {
    const sa = eventStatus(a, now), sb = eventStatus(b, now)
    if (sa !== sb) return rank[sa] - rank[sb]
    const ta = +new Date(a.startsAtIso), tb = +new Date(b.startsAtIso)
    return sa === 'ended' ? tb - ta : ta - tb
  })
}

export function eventStoreUrl(eventId: string): string {
  return `${APP_STORE_URL}?eventid=${eventId}`
}

export function eventBySlug(slug: string): AppEvent | undefined {
  return EVENTS.find((e) => e.slug === slug)
}

export function otherEvents(slug: string): AppEvent[] {
  return EVENTS.filter((e) => e.slug !== slug)
}
